import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Flex } from 'antd';
import { ChatbotPanel, type ChatbotMessage } from '../ChatbotPanel';
import type { ChatSource } from '../ReferenceCards';
import { Button } from '../../Button';
import { ChatEmptyState } from '../../ChatEmptyState';
import { ChatLoadingState } from '../../ChatLoadingState';
import { SuggestedPromptChip } from '../../SuggestedPromptChip';
import { colors, spacing } from '../../../theme-v2';

/**
 * Chatbot state the panel renders — matches the shape returned by
 * experience-viewer's `useExperienceChatbot`, kept structural here so libs/ui
 * stays free of an experience-viewer dependency.
 */
export interface ExperienceChatbotState {
  messages: ChatbotMessage[];
  /** `source` marks where the query came from (typed input vs a suggestion chip). */
  onSubmit: (text: string, source?: 'user' | 'suggested') => void;
  /** Fired when the chat input gains focus. */
  onInputFocus?: () => void;
  submitting: boolean;
  /** Assistant follow-up / initial suggested questions. */
  suggestions: string[];
  /** True until the handshake delivers the greeting and its first prompts. */
  loading: boolean;
  /** The handshake errored or timed out; nothing arrives without a retry. */
  failed: boolean;
  /** Reconnect after a failed handshake. Omitted = unrecoverable failure (no retry button). */
  onRetry?: () => void;
}

export interface ExperienceChatbotPanelProps {
  /** Driven by `useExperienceChatbot`. */
  chat: ExperienceChatbotState;
  /** Section title ("Chatbot"), consumer-translated. */
  title: ReactNode;
  /** Input placeholder, consumer-translated. */
  placeholder?: string;
  /** Send-button aria-label, consumer-translated. */
  sendAriaLabel: string;
  /** Empty-state hero title, consumer-translated. */
  emptyStateTitle: ReactNode;
  /** Empty-state hero subtitle, consumer-translated. */
  emptyStateSubtitle?: ReactNode;
  /** Fired when a reference card under an answer is clicked. */
  onSourceClick?: (source: ChatSource) => void;
  /** Id of the document currently shown — drives the "other document" label. */
  displayedDocumentId?: string;
  /** All documents, to resolve a source's document name. */
  documents?: { id: string; name?: string }[];
  /**
   * Collapse state of the panel, forwarded straight to `<ChatbotPanel>`.
   * `ExperiencePageLayout` injects this (with `onOpenChange`) into whatever
   * element fills its `chatbot` slot, so it must be accepted AND passed
   * down — dropping it leaves the panel's disclosure uncontrolled and it
   * keeps full height while the layout believes it collapsed.
   */
  open?: boolean;
  /** Fired when the panel's header toggle is clicked. See `open`. */
  onOpenChange?: (open: boolean) => void;
  /** Whether a collapse toggle is offered. Injected by the layout; forwarded
   *  like `open`. */
  collapsible?: boolean;
}

/**
 * `<ChatbotPanel>` pre-wired to a `useExperienceChatbot` result: renders the
 * assistant's suggestions as `SuggestedPromptChip`s — inside the empty state
 * while the thread is empty, and in the follow-up slot once a conversation
 * exists (never both). Shared by every app's experience page so the wiring
 * lives in one place instead of being copied per app.
 *
 * Owns the thread's pre-conversation states: a skeleton while the chatbot
 * completes its handshake, a retry prompt if that handshake never lands, and
 * the suggested-prompt empty state once it does.
 */
export const ExperienceChatbotPanel = ({
  chat,
  title,
  placeholder,
  sendAriaLabel,
  emptyStateTitle,
  emptyStateSubtitle,
  onSourceClick,
  displayedDocumentId,
  documents,
  open,
  onOpenChange,
  collapsible,
}: ExperienceChatbotPanelProps) => {
  const { t } = useTranslation(undefined, { useSuspense: false });

  // The greeting and the initial suggested questions ride in the same first
  // frame from the backend, so there is nothing worth showing before it lands
  // — a skeleton beats an empty hero with no prompts under it.
  const renderChatContent = () => {
    if (chat.failed) {
      return (
        <Flex
          vertical
          align="center"
          justify="center"
          gap={spacing.md}
          style={{
            height: '100%',
            padding: spacing.lg,
            textAlign: 'center',
            color: colors.text.secondary,
          }}
        >
          {chat.onRetry ? (
            <>
              {t('experienceV2.chatbot.error.message')}
              <Button variant="secondary" size="s" onClick={chat.onRetry}>
                {t('experienceV2.chatbot.error.retry')}
              </Button>
            </>
          ) : (
            t('experienceV2.chatbot.error.generationFailed')
          )}
        </Flex>
      );
    }
    if (chat.loading) return <ChatLoadingState />;
    return (
      <ChatEmptyState
        title={emptyStateTitle}
        subtitle={emptyStateSubtitle}
        prompts={chat.suggestions.map((q) => ({ id: q, label: q }))}
        onPromptSelect={(p) => chat.onSubmit(p.id, 'suggested')}
      />
    );
  };

  return (
    <ChatbotPanel
      title={title}
      messages={chat.messages}
      onSubmit={chat.onSubmit}
      onInputFocus={chat.onInputFocus}
      submitting={chat.submitting}
      placeholder={placeholder}
      sendAriaLabel={sendAriaLabel}
      onSourceClick={onSourceClick}
      displayedDocumentId={displayedDocumentId}
      documents={documents}
      open={open}
      onOpenChange={onOpenChange}
      collapsible={collapsible}
      followUps={
        // Only once a conversation exists — while the thread is empty the same
        // suggestions render inside the empty state, never both at once.
        chat.messages.length > 0 && chat.suggestions.length > 0
          ? chat.suggestions.map((q) => (
              <SuggestedPromptChip
                key={q}
                onClick={() => chat.onSubmit(q, 'suggested')}
              >
                {q}
              </SuggestedPromptChip>
            ))
          : undefined
      }
      emptyState={renderChatContent()}
    />
  );
};
